import React, { useContext, useEffect, useMemo } from "react";

import { Preview } from "@creatomate/preview";
import { Button, Dropdown, Icon, PlainButton } from "@culturehq/components";

import { Trans } from "app/utils";

import SelectVideoFrame from "components/SelectVideoFrame";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";

interface ThumbnailOptionsProps {
  preview: Preview;
}

const ThumbnailOptions: React.FC<ThumbnailOptionsProps> = ({ preview }) => {
  const { data, handleAutosaveDraft } = useContext(CreatomateContext);
  const { selectedThumbnail, story } = data;
  const isSelectVideoFrameShow = story?.media?.mediaType === "video" && !!story.media?.url;

  const thumbUrl = useMemo(() => {
    if (typeof selectedThumbnail === "string" && selectedThumbnail.startsWith("http")) {
      return selectedThumbnail as string;
    }
    if (selectedThumbnail instanceof Blob) {
      return URL.createObjectURL(selectedThumbnail);
    }
    return story?.media?.thumbnail || story?.thumbnail || null;
  }, [selectedThumbnail, story]);

  useEffect(() => (
    () => {
      if (selectedThumbnail instanceof Blob && thumbUrl) URL.revokeObjectURL(thumbUrl);
    }
  ), [selectedThumbnail, thumbUrl]);

  const handleSelectedFrame = (thumbnail: Blob) => {
    // Revoke the old URL before creating a new one
    if (selectedThumbnail instanceof Blob && thumbUrl) {
      URL.revokeObjectURL(thumbUrl);
    }

    handleAutosaveDraft({ selectedThumbnail: thumbnail });
  };

  const handleUploadNewImage = () => {
    const inputElement = document.createElement("input");
    inputElement.type = "file";
    inputElement.accept = "image/*";
    inputElement.id = "updload-thumbnail"; // Add a custom ID
    inputElement.style.display = "none"; // Hide the input element
    inputElement.addEventListener("change", (event: Event) => {
      const target = event.target as HTMLInputElement;
      const archive = target.files && target.files[0];
      if (archive) handleAutosaveDraft({ selectedThumbnail: archive });
    });

    document.body.appendChild(inputElement); // Add to the DOM
    inputElement.click();

    setTimeout(() => {
      document.body.removeChild(inputElement); // Clean up after selection
    }, 5000); // Adjust the delay as needed
  };

  const handlePauseParent = () => {
    preview.pause();
  };

  const handleSelectThumbnail = (_selectedValue: string) => { };

  return (
    <div className="Captions-Header__Title">
      <div>
        {!story && (
          <Button onClick={handleUploadNewImage} inverted>
            <Icon icon="image-add" />
            <Trans>Upload new image</Trans>
          </Button>
        )}
        {story && (
          <Dropdown className="Dropdown-Option-Sub" onChange={handleSelectThumbnail} selected="upload_new_image">
            <Dropdown.Button className="Option-Subtitles-btn">
              <span className="DownloadDp-Text"><Trans>Choose Image</Trans></span>
            </Dropdown.Button>
            <Dropdown.ListBox className="DownloadTh-Options">
              <Dropdown.Option
                className="DownloadDp-Option"
                value="upload_new_image"
              >
                <PlainButton onClick={handleUploadNewImage}>
                  <Icon icon="image-add" />
                  <Trans>Upload new image</Trans>
                </PlainButton>
              </Dropdown.Option>
              {isSelectVideoFrameShow && (
                <Dropdown.Option
                  className="DownloadDp-Option"
                  value=""
                >
                  <SelectVideoFrame
                    onFrameSelected={handleSelectedFrame}
                    onPauseParent={handlePauseParent}
                    story={story}
                    thumbnail={thumbUrl}
                    preview={preview}
                  />
                </Dropdown.Option>
              )}
            </Dropdown.ListBox>
          </Dropdown>
        )}
      </div>
    </div>
  );
};

export default ThumbnailOptions;
